import { useCallback, useEffect, useMemo, useState } from "react";
import type { Zone } from "../types";
import { fetchAllCityZones } from "../api/cityZones";
import { ignoreAbort } from "../utils/errors";

type Args = {
  cityZoneIds: string[];
  onChangeIds: (ids: string[]) => void; // setCityZoneIds
};

export function useCityZones({ cityZoneIds, onChangeIds }: Args) {
  const [allCityZones, setAllCityZones] = useState<Zone[] | null>(null);
  const [activeCityZoneId, _setActiveCityZoneId] = useState<string | null>(
    null
  );

  //  Fetch all city zones once
  useEffect(() => {
    let alive = true;
    fetchAllCityZones()
      .then((zones) => {
        if (!alive) return;
        setAllCityZones(zones);

        if (!activeCityZoneId && cityZoneIds.length > 0) {
          _setActiveCityZoneId(cityZoneIds[0]);
        }
      })
      .catch(ignoreAbort);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allCityZonesById = useMemo(() => {
    if (!allCityZones) return new Map<string, Zone>();
    return new Map(allCityZones.map((z) => [String(z.id), z]));
  }, [allCityZones]);

  //  Selected city zones
  const selectedCityZones = useMemo(() => {
    if (!allCityZones) return [];
    return cityZoneIds
      .map((id) => allCityZonesById.get(String(id)))
      .filter(Boolean) as Zone[];
  }, [allCityZones, allCityZonesById, cityZoneIds]);

  const setActiveCityZoneId = useCallback((next: string | null) => {
    _setActiveCityZoneId((cur) => (cur === next ? cur : next));
  }, []);

  useEffect(() => {
    if (!selectedCityZones.length) {
      if (activeCityZoneId) _setActiveCityZoneId(null);
      return;
    }

    if (
      activeCityZoneId &&
      !selectedCityZones.some((z) => z.id === activeCityZoneId)
    ) {
      _setActiveCityZoneId(null);
      return;
    }

    if (!activeCityZoneId && selectedCityZones.length > 0) {
      _setActiveCityZoneId(selectedCityZones[0].id);
    }
  }, [selectedCityZones, activeCityZoneId]);

  const activeCityZone = useMemo<Zone | null>(() => {
    if (!activeCityZoneId) return null;
    return (
      selectedCityZones.find(
        (z) => String(z.id) === String(activeCityZoneId)
      ) ?? null
    );
  }, [selectedCityZones, activeCityZoneId]);

  const addAndActivateCityZone = useCallback(
    (zone: Zone | string) => {
      if (!zone) return;
      const id = typeof zone === "string" ? String(zone) : String(zone.id);

      const next = cityZoneIds.includes(id)
        ? cityZoneIds
        : [id, ...cityZoneIds];
      onChangeIds(next);

      _setActiveCityZoneId(id);
    },
    [cityZoneIds, onChangeIds]
  );

  const removeCityZone = useCallback(
    (idRaw: string) => {
      const id = String(idRaw);
      const next = cityZoneIds.filter((x) => x !== id);
      onChangeIds(next);
      _setActiveCityZoneId((cur) => (cur === id ? null : cur));
    },
    [cityZoneIds, onChangeIds]
  );

  return {
    cityZoneIds,
    selectedCityZones,
    allCityZones,
    allCityZonesById,
    allCityZonesLoaded: Boolean(allCityZones),

    activeCityZoneId,
    setActiveCityZoneId,
    activeCityZone,

    addAndActivateCityZone,
    removeCityZone,
  };
}
